import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {HaliRenderOne, useHalStates} from '@synako/haligator';
import {useLivraisonsPossiblesContext} from 'ff-common';
import {useAffichagePrixTtc, useTotalPaniers} from 'ff-common';
import {Trans, useTranslation} from 'react-i18next';
import {Box, Button, HStack, Image, Text, VStack} from '@chakra-ui/react';
import {
  useClient,
  useLivraisons,
  useSocieteCommerciale,
} from '@/haligator-factory';
import {ModaleLayout} from './ModaleLayout';
import {OrderModalIcon} from '@/components/svg/OrderModalIcon';
import {IconeTemperatureLivraison} from '@/components/IconeTemperatureLivraison';
import {FollowPromiseOne, State} from '@synako/halitrek';
import {
  LivraisonDesc,
  MarketplaceDesc,
  PanierDesc,
} from 'api-types/ffconnect-hal';
import {OuvrirPanierModale} from './CalendarModale/OuvrirPanierModale';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {CheckIcon} from '@chakra-ui/icons';
import {getModeTheme} from '@/utils/appMode';
export const ListePaniersModale = NiceModal.create(() => {
  const modal = useModal();
  const {t} = useTranslation();
  const {modeColor} = getModeTheme();
  const {
    setLivraisonSelectionnee,
    livraisonSelectionnee,
    selectedMarketplaceId,
  } = useLivraisonsPossiblesContext();

  const livraisonsPossibles = useHalStates(useLivraisons().followAll('items'));
  const societeCommerciale = useSocieteCommerciale();
  const client = useClient();

  const marketplacesHook = useHalStates(
    client.follow('marketplaces').followAll('items'),
  );
  const marketplaceStates = marketplacesHook.states;

  if (!livraisonsPossibles.states || livraisonsPossibles.error) {
    return null;
  }

  const livraisonsAvecPanier = livraisonsPossibles.states.filter((livraison) =>
    livraison.hasOneLink('panier_ouvert'),
  );

  // Séparer la date sélectionnée des autres paniers
  const selectedDateId = livraisonSelectionnee.data.id;
  const selectedDateCart = livraisonsAvecPanier.find(
    (livraison) => livraison.data.id === selectedDateId,
  );
  const otherCarts = livraisonsAvecPanier.filter(
    (livraison) => livraison.data.id !== selectedDateId,
  );

  const totalPaniers = useTotalPaniers(client);

  const handleSelectPanier = (livraisonState: State<LivraisonDesc>) => {
    setLivraisonSelectionnee(livraisonState);
    modal.remove();
  };

  const header = (
    <HStack alignItems='center'>
      <Box
        bg={modeColor('blue.main_transparency_10', 'red.assistance')}
        borderRadius='4px'
        w='20px'
        h='20px'
        display='flex'
        alignItems='center'
        justifyContent='center'
        mx='1'
      >
        <Text
          fontSize='sm'
          fontWeight='bold'
          color={modeColor('blue.main', 'white')}
        >
          {totalPaniers}
        </Text>
      </Box>
      <Text
        color='black.text'
        fontWeight='700'
      >
        {t('panier.paniers_actifs', {count: totalPaniers})}
      </Text>
    </HStack>
  );

  return (
    <ModaleLayout
      visible={modal.visible}
      hide={modal.remove}
      titre={header}
      contentStyle={{width: '100%'}}
    >
      <VStack
        alignItems='stretch'
        spacing={4}
      >
        <HaliRenderOne
          nav={societeCommerciale}
          render={({data}) => (
            <LogoEtNom
              logoSrc={imageOptimisee(data.url_logo, 96)}
              nom={data.tolede.nom_commercial}
            />
          )}
        />

        <Button
          variant='outline'
          borderColor='blue.main'
          onClick={() => {
            NiceModal.show(OuvrirPanierModale);
          }}
          leftIcon={<OrderModalIcon color='blue.main' />}
          textColor='blue.main'
        >
          {t('modale_livraison.header.ouvrir')}
        </Button>

        <VStack spacing={2}>
          {/* Toujours afficher la date sélectionnée en premier */}
          <PanierItem
            key={livraisonSelectionnee.data.id}
            livraisonState={livraisonSelectionnee}
            onSelect={handleSelectPanier}
            hasPanier={!!selectedDateCart}
            isSelected={!selectedMarketplaceId}
          />
          {/* Afficher les paniers pour les autres dates */}
          {otherCarts.map((livraisonState) => (
            <PanierItem
              key={livraisonState.data.id}
              livraisonState={livraisonState}
              onSelect={handleSelectPanier}
            />
          ))}
        </VStack>

        {/* Section marketplace */}
        {marketplaceStates && marketplaceStates.length > 0 && (
          <VStack spacing={2}>
            {marketplaceStates.map((marketplaceState) => (
              <MarketplacePanierItem
                key={marketplaceState.data.marketplace_id}
                marketplaceState={marketplaceState}
                onClose={modal.remove}
              />
            ))}
          </VStack>
        )}
      </VStack>
    </ModaleLayout>
  );
});

const LogoEtNom: React.FC<{logoSrc?: string; nom: string}> = ({
  logoSrc,
  nom,
}) => (
  <HStack
    alignItems='center'
    gap={3}
  >
    {logoSrc && (
      <Image
        src={logoSrc}
        alt={nom}
        boxSize='40px'
        objectFit='contain'
      />
    )}
    <Text
      fontSize='lg'
      fontWeight='bold'
      color='gray.dark'
    >
      {nom}
    </Text>
  </HStack>
);

const PanierStats: React.FC<{
  count: number;
  totalEstimeHt: string;
  totalEstimeTtc: string;
}> = ({count, totalEstimeHt, totalEstimeTtc}) => {
  const {t} = useTranslation();
  const affichagePrixTtc = useAffichagePrixTtc(useClient());
  return (
    <HStack
      alignItems='center'
      gap={2}
    >
      <Box
        bg='gray.light'
        color='gray.dark'
        borderRadius='8px'
        px={2}
        py={1}
      >
        <Text fontWeight='600'>{t('global.produit', {count})}</Text>
      </Box>
      <Text>
        <Text
          as='span'
          fontWeight='600'
        >
          {affichagePrixTtc ? totalEstimeTtc : totalEstimeHt}
        </Text>{' '}
        €
      </Text>
    </HStack>
  );
};

const PanierCard: React.FC<{
  isSelected: boolean;
  header: React.ReactNode;
  children: React.ReactNode;
  onSelect: () => void;
}> = ({isSelected, header, children, onSelect}) => {
  const {t} = useTranslation();
  return (
    <Box
      border={'1px solid'}
      borderColor='gray.light'
      borderRadius='8px'
      p={3}
      w='100%'
    >
      <HStack
        alignItems='center'
        gap={2}
        mb={2}
      >
        {header}
      </HStack>
      <HStack
        alignItems='center'
        justifyContent='space-between'
      >
        {children}
        <Button
          variant='outline'
          borderColor='blue.main'
          color='blue.main'
          rightIcon={
            isSelected ? (
              <CheckIcon
                color='blue.main'
                h='15px'
              />
            ) : undefined
          }
          opacity={isSelected ? 0.5 : 1}
          onClick={onSelect}
        >
          {t(isSelected ? 'global.selectionne' : 'global.selectionner')}
        </Button>
      </HStack>
    </Box>
  );
};

const PanierItem: React.FC<{
  livraisonState: State<LivraisonDesc>;
  onSelect: (livraisonState: State<LivraisonDesc>) => void;
  hasPanier?: boolean;
  isSelected?: boolean;
}> = ({livraisonState, onSelect, hasPanier = true, isSelected = false}) => {
  const {t} = useTranslation();
  const dateDeLivraison = livraisonState.data.tolede.date_de_livraison;
  const header = (
    <>
      {livraisonState.data.temperature_effective && (
        <IconeTemperatureLivraison
          temperature_tournee={livraisonState.data.temperature_effective}
          boxSize={5}
        />
      )}
      <Text>
        <Trans t={t}>
          {t('panier.pour_la_livraison_du_mobile', {
            date: new Date(dateDeLivraison),
            formatParams: {
              date: {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              },
            },
          })}
        </Trans>
      </Text>
    </>
  );
  return (
    <PanierCard
      isSelected={isSelected}
      header={header}
      onSelect={() => {
        onSelect(livraisonState);
      }}
    >
      {hasPanier ? (
        <LoadPanierInfo panier={livraisonState.follow('panier_ouvert')} />
      ) : (
        <PanierInfoZero />
      )}
    </PanierCard>
  );
};

const LoadPanierInfo: React.FC<{panier: FollowPromiseOne<PanierDesc>}> = ({
  panier,
}) => (
  <HaliRenderOne
    nav={panier}
    render={({data}) => (
      <PanierStats
        count={data.nombre_de_lignes}
        totalEstimeHt={String(data.total_estime_ht ?? '')}
        totalEstimeTtc={String(data.total_estime_ttc)}
      />
    )}
  />
);

const PanierInfoZero: React.FC = () => (
  <PanierStats
    count={0}
    totalEstimeHt='0'
    totalEstimeTtc='0'
  />
);

const MarketplacePanierItem: React.FC<{
  marketplaceState: State<MarketplaceDesc>;
  onClose: () => void;
}> = ({marketplaceState, onClose}) => {
  const {t} = useTranslation();
  const {setSelectedMarketplaceId, selectedMarketplaceId} =
    useLivraisonsPossiblesContext();
  const client = useClient();
  const marketplace_id = marketplaceState.data.marketplace_id;
  const isSelected = selectedMarketplaceId === marketplace_id;

  const paniersHook = useHalStates(
    client
      .follow('paniers', {type_panier: marketplace_id, statut: 'ouvert'})
      .followAll('items'),
  );

  const panierState = paniersHook.states?.[0];
  if (!panierState) {
    return null;
  }

  const handleSelect = () => {
    setSelectedMarketplaceId(marketplace_id);
    onClose();
  };

  const logoSrc = marketplaceState.data.logo_vertical_couleur
    ? `${import.meta.env['VITE_MEDIA_URL']}${marketplaceState.data.logo_vertical_couleur}`
    : undefined;

  return (
    <VStack
      alignItems='start'
      alignSelf='stretch'
      spacing={2}
    >
      <LogoEtNom
        logoSrc={logoSrc}
        nom={marketplaceState.data.nom}
      />
      <PanierCard
        isSelected={isSelected}
        header={
          <Text
            fontWeight='400'
            color='gray.dark'
          >
            {t('panier.livraison_48h_72h')}
          </Text>
        }
        onSelect={handleSelect}
      >
        <PanierStats
          count={panierState.data.nombre_de_lignes}
          totalEstimeHt={String(panierState.data.total_estime_ht ?? '')}
          totalEstimeTtc={String(panierState.data.total_estime_ttc)}
        />
      </PanierCard>
    </VStack>
  );
};
